/* eslint-disable check-file/filename-blocklist */
import { defineMessages } from 'react-intl';

import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

const i18nBase = defineMessages({
	teamPresenceTitle: {
		id: 'content-state.team-presence.title',
		defaultMessage: 'Avatars on published content',
		description: 'Title text next to toggle to enable team presence on space settings.',
	},
	teamPresenceDescription: {
		id: 'content-state.team-presence.description',
		defaultMessage:
			'When turned on, viewers can see who else is viewing published pages and blog posts. <a>Learn more about avatar visibility</a>',
		description: 'Description below toggle to enable team presence on space settings.',
	},
	teamPresenceDisabledTooltip: {
		id: 'content-state.team-presence.tooltip.disabled',
		defaultMessage: 'Turned off by site admin',
		description:
			'Tooltip when team presence toggle on space settings is disabled due to site settings.',
	},
	enableContentStateTitle: {
		id: 'content-state.enable.content.state.title',
		description:
			'Title within space admin area which is next to a toggle button where space admin can enable or disable content statuses.',
		defaultMessage: 'Content statuses',
	},
	enableContentStateBody: {
		id: 'content-state.enable.content.state.body',
		description:
			'Text within space admin area which informs user how the toggle will enable or disable content statuses.',
		defaultMessage:
			'Turn off to hide the ability to add a status when a page or blog post is created',
	},
	suggestedStateSuggestionsTitle: {
		id: 'content-state.default.status.suggestions.title',
		description:
			'The text is shown as a heading above the status customization area in the space admin configuration page, indicating where administrators can manage default statuses for the space.',
		defaultMessage: 'Suggest statuses',
	},
	suggestedStateSuggestionsBody: {
		id: 'content-state.default.status.suggestions.body',
		description:
			'Text above custom status areas which tells user that they can customize statuses and how many they can customize',
		defaultMessage: 'Decide what statuses are shown by default to users in this space.',
	},
	suggestedStateSuggestionsBodyExpanded: {
		id: 'content-state.default.status.suggestions.body.expanded',
		description:
			'Text above custom status areas which tells user that they can customize statuses and how many they can customize - expanded',
		defaultMessage: 'Decide what statuses are shown by default to users in this space.',
	},
	restoreDefaults: {
		id: 'content-state.default.status.suggestions.body.restore.default',
		description:
			'Text above custom status areas which allows user to restore the default statuses.',
		defaultMessage: 'Restore defaults',
	},
	allowCustomContentStatesTitle: {
		id: 'content-state.allow.custom.content.states.title',
		description:
			'Title within space admin area which is next to a toggle button where space admin can enable or disable custom page statuses.',
		defaultMessage: 'Create custom statuses',
	},
	allowCustomContentStatesBody: {
		id: 'content-state.allow.custom.content.states.body',
		description:
			'Text within space admin area which informs user how the toggle will enable or disable custom page statuses.',
		defaultMessage: 'Allow authors to create their own custom statuses',
	},
	roughDraft: {
		id: 'content-state.default.status.rough.draft',
		description:
			'One of three default statuses shown if space admin has not customized page status',
		defaultMessage: 'Rough draft',
	},
	inProgress: {
		id: 'content-state.default.status.in.progress',
		description:
			'One of three default statuses shown if space admin has not customized page status',
		defaultMessage: 'In progress',
	},
	readyForReview: {
		id: 'content-state.default.status.ready.for.review',
		description:
			'One of three default statuses shown if space admin has not customized page status',
		defaultMessage: 'Ready for review',
	},
	verified: {
		id: 'content-state.default.status.verified',
		description: 'One of the default statuses shown if space admin has not customized page status',
		defaultMessage: 'Verified',
	},
	save: {
		id: 'content-state.save.status.button.text',
		description: 'Message on button which user clicks on to save their page status',
		defaultMessage: 'Save',
	},
	cancel: {
		id: 'content-state.cancel.text',
		description: "Message on button which user clicks on to cancel any changes they've made",
		defaultMessage: 'Cancel',
	},
	addContentStatePlaceholder: {
		id: 'content-state.status.maker.add.status.placeholder.text',
		description: 'Placeholder text in text field where user types a page status',
		defaultMessage: 'Add status',
	},
	createStatus: {
		id: 'content-state.create.status.button.text',
		description: 'Message on button which allows space admin to create a content state',
		defaultMessage: 'Create a status',
	},
	restoreDefaultSuggestions: {
		id: 'content-state.restore.default.suggestions',
		description: 'Title of modal which allows user to restore back the default page states',
		defaultMessage: 'Restore default suggestions',
	},
	confirm: {
		id: 'content-state.restore.default.suggestions.confirm',
		description:
			'Text inside of confirm button which users click to confirm the restoration of the default states',
		defaultMessage: 'Confirm',
	},
	flagTitle: {
		id: 'content-state.space.admin.duplicate.status.flag.title',
		description: 'Title of flag if status they added is a duplicate',
		defaultMessage: 'The status you created already exists',
	},
	flagDescription: {
		id: 'content-state.space.admin.duplicate.status.flag.description',
		description: 'Description of flag if status they added is a duplicate',
		defaultMessage:
			'Statuses with the same text and color indicator are considered duplicates and aren’t created.',
	},
	remove: {
		id: 'content-state.default.status.remove.tooltip',
		description: 'Tooltip on trash icon that tells user they can remove a custom status',
		defaultMessage: 'Remove',
	},
	moreInfoMessage: {
		id: 'content-state.more.info.message',
		description:
			'Tooltip on info icon that tells user what happens when the remove a content state',
		defaultMessage: 'Removing a suggestion won’t impact pages or posts where it was used.',
	},
});

const i18nLiveDocs = defineMessages({
	enableContentStateBody: {
		id: 'content-state.enable.content.state.body.livedocs',
		defaultMessage:
			'Turn off to hide the ability to add a status when a page, live doc, or blog post is created',
		description: 'Updated version of content-state.enable.content.state.body',
	},

	moreInfoMessage: {
		id: 'content-state.more.info.message.livedocs',
		defaultMessage: 'Removing a suggestion won’t impact pages, docs, or posts where it was used.',
		description: 'Updated version of content-state.more.info.message',
	},
});

export const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);
